import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { ArrowRight, ArrowLeft, LogOut } from 'lucide-react'
import { checkEmailAvailability, provisionAssistant, provisionEmail } from './api'
import { personalizedPresets, assistantNameToEmail } from './string'
import { LanguageStep, YourNameStep, AIConsentStep, ProvisioningStep } from './components'
import { useAuth } from '../auth'
import { ApiError } from '../../shared/api/base'

interface OnboardingProps {
  onComplete: (language: string) => void
}

export default function Onboarding({ onComplete }: OnboardingProps) {
  const { t, i18n } = useTranslation('onboarding')
  const { signOut, user } = useAuth()

  // Extract name from auth provider metadata (Apple / Google sign-in)
  const authFirstName = user?.user_metadata?.given_name ?? user?.user_metadata?.name?.split(' ')[0] ?? ''
  const authLastName = user?.user_metadata?.family_name ?? ''

  const [step, setStep] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Step 0: language — default to browser locale
  const [language, setLanguage] = useState(() =>
    i18n.resolvedLanguage?.startsWith('sv') ? 'sv' : 'en'
  )

  // Step 1: user's name — pre-fill from auth provider metadata (Apple/Google)
  const [userFirstName, setUserFirstName] = useState(authFirstName)
  const [userLastName, setUserLastName] = useState(authLastName)

  // Step 2: AI consent (no state needed — just an agree button)

  // Step 3: provisioning
  const [provisioning, setProvisioning] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [isAuthError, setIsAuthError] = useState(false)
  const [provisionedEmail, setProvisionedEmail] = useState<string | null>(null)

  // Derived: default assistant name from first name + language (e.g. "Daniel's Assistant").
  // User can rename in Settings later.
  const presetNames = personalizedPresets(userFirstName.trim(), language)
  const defaultAssistantName = presetNames[0]?.name ?? 'TendBot'
  const defaultEmailLocal = assistantNameToEmail(defaultAssistantName)

  const canProceedYourName = userFirstName.trim().length > 0

  // ─── Navigation ──────────────────────────────────────────

  // Skip the name step if auth provider already supplied the user's name
  const hasAuthName = !!(authFirstName)

  const goNext = () => {
    setIsTransitioning(true)
    setTimeout(() => {
      setStep(s => {
        const next = s + 1
        // Skip name step (1) if auth provider already provided it
        if (next === 1 && hasAuthName) return 2
        return next
      })
      setIsTransitioning(false)
    }, 250)
  }

  const goBack = () => {
    setIsTransitioning(true)
    setTimeout(() => {
      setStep(s => {
        const prev = s - 1
        // Skip name step (1) going back if auth provider already provided it
        if (prev === 1 && hasAuthName) return 0
        return prev
      })
      setIsTransitioning(false)
    }, 250)
  }

  // ─── Provisioning ────────────────────────────────────────

  const handleProvision = async () => {
    setProvisioning('loading')
    setErrorMessage('')
    setIsAuthError(false)
    setProvisionedEmail(null)

    try {
      // Read captured UTM / referral data
      let signupSource: Record<string, string> | undefined
      try {
        const raw = sessionStorage.getItem('tendbot-utm')
        if (raw) signupSource = JSON.parse(raw)
      } catch { /* ignore */ }

      await provisionAssistant(
        language,
        userFirstName.trim() || undefined,
        userLastName.trim() || undefined,
        defaultAssistantName,
        signupSource,
      )

      // Best-effort email provisioning. Default is derived from the assistant name;
      // on collision, pick the first available variant the backend suggests.
      if (defaultEmailLocal) {
        try {
          let emailLocal = defaultEmailLocal
          const availability = await checkEmailAvailability(defaultEmailLocal).catch(() => null)
          if (availability && !availability.available) {
            emailLocal = availability.suggestions?.[0] ?? ''
          }
          if (emailLocal) {
            await provisionEmail(emailLocal, defaultAssistantName)
            setProvisionedEmail(`${emailLocal}@tend.bot`)
          }
        } catch { /* not critical — user can set one in Settings */ }
      }

      sessionStorage.removeItem('tendbot-utm')
      setProvisioning('success')
      setTimeout(() => onComplete(language), 2000)
    } catch (err) {
      setProvisioning('error')
      const authError = (err instanceof ApiError && err.status === 401) ||
        (err instanceof Error && err.message === 'Not authenticated')
      setIsAuthError(authError)
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong')
    }
  }

  const handleAgreeAndProvision = () => {
    goNext()
    setTimeout(() => handleProvision(), 300)
  }

  // ─── Keyboard handling ────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || step >= 2 || isTransitioning) return
      if (step === 1 && !canProceedYourName) return
      e.preventDefault()
      goNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [step, isTransitioning, canProceedYourName]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync language on mount
  useEffect(() => {
    i18n.changeLanguage(language)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang)
    i18n.changeLanguage(lang)
  }

  // ─── Render ───────────────────────────────────────────────

  const INTERACTIVE_STEPS = 3 // steps 0-2 show progress dots

  return (
    <div className="flex flex-col items-center justify-center min-h-full px-6 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm"
      >
        {/* Progress dots */}
        {step < INTERACTIVE_STEPS && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {Array.from({ length: INTERACTIVE_STEPS }, (_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step ? 'w-6 bg-gold' : i < step ? 'w-1.5 bg-gold/50' : 'w-1.5 bg-slate-mid'
                }`}
              />
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {!isTransitioning && (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {step === 0 && (
                <LanguageStep language={language} onSelect={handleLanguageChange} />
              )}
              {step === 1 && (
                <YourNameStep
                  firstName={userFirstName}
                  lastName={userLastName}
                  onFirstNameChange={setUserFirstName}
                  onLastNameChange={setUserLastName}
                />
              )}
              {step === 2 && (
                <AIConsentStep
                  onAgree={handleAgreeAndProvision}
                  onBack={goBack}
                />
              )}
              {step === 3 && (
                <ProvisioningStep
                  status={provisioning}
                  errorMessage={errorMessage}
                  isAuthError={isAuthError}
                  assistantName={defaultAssistantName}
                  emailAddress={provisionedEmail ?? undefined}
                  onRetry={handleProvision}
                  onSignOut={signOut}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation — shown for steps 0-1 (step 2 has its own, step 3 is provisioning) */}
        {step < 2 && (
          <div className="flex items-center justify-between mt-8">
            {step > 0 ? (
              <button
                onClick={goBack}
                className="flex items-center gap-1.5 text-pearl/50 hover:text-pearl text-sm transition-colors"
              >
                <ArrowLeft size={16} />
                {t('nav.back')}
              </button>
            ) : (
              <div />
            )}
            <button
              onClick={goNext}
              disabled={step === 1 && !canProceedYourName}
              className="flex items-center gap-2 bg-gold hover:bg-gold-light text-midnight font-semibold px-6 py-2.5 rounded-xl transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {t('nav.continue')}
              <ArrowRight size={16} />
            </button>
          </div>
        )}
      </motion.div>

      {/* Subtle logout */}
      {step < 3 && (
        <button
          onClick={signOut}
          className="fixed bottom-[calc(env(safe-area-inset-bottom)+12px)] left-4 flex items-center gap-1.5 text-pearl hover:text-ivory text-xs transition-colors"
        >
          <LogOut size={12} />
          {t('nav.logout')}
        </button>
      )}
    </div>
  )
}
